import { Box, FileText, Film, ImageOff } from 'lucide-react';

import type { Media } from '@nucleus-commerce/api-client/admin';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export interface MediaThumbProps {
  /** Null renders the empty placeholder (no featured image yet). */
  media: Media | null | undefined;
  /** Rendition to prefer; falls back to the original `url` while the worker has not run. */
  rendition?: 'thumb' | 'medium' | 'large';
  className?: string;
}

const KIND_ICON = { video: Film, model_3d: Box, document: FileText } as const;

/** Square preview of a media row. Non-image kinds show an icon instead of a broken image. */
export function MediaThumb({ media, rendition = 'thumb', className }: MediaThumbProps) {
  const base =
    'bg-muted text-muted-foreground flex aspect-square items-center justify-center overflow-hidden rounded-md';
  if (!media) {
    return (
      <div className={cn(base, 'border border-dashed', className)} aria-hidden>
        <ImageOff className="size-4" />
      </div>
    );
  }
  if (media.kind !== 'image') {
    const Icon = KIND_ICON[media.kind];
    return (
      <div className={cn(base, className)} role="img" aria-label={media.alt ?? media.kind}>
        <Icon className="size-5" aria-hidden />
      </div>
    );
  }
  const src = media.renditions?.[rendition] ?? media.url;
  return (
    <div className={cn(base, className)}>
      <img src={src} alt={media.alt ?? ''} className="size-full object-cover" loading="lazy" />
    </div>
  );
}
